'use client';

import { type PropsWithChildren, useEffect, useMemo } from 'react';
import {
  useGetThemeQuery,
  useOnThemeChangedSubscription,
} from '@frameio/graphql-client/graphql/features/integrations';
import { VaporProvider } from '@frameio/shared/contexts/AppUiThemeContext';
import { updateDomThemeAttributes } from '@frameio/shared/contexts/AppUiThemeContext/handleDomMutations';
import { isServer } from '@frameio/utils/config/helpers';
import type { Themes } from '@frameio/vapor';
import { themeClassNames as THEME_CLASS_NAMES } from '@frameio/vapor';
import styles from '../../_components/body.module.css';
import TEXT from './generated/TEXT/ThemeProvider';

const lightTheme: Themes = 'light-neutral';
const darkTheme: Themes = 'dark-neutral';

TEXT.DEFINE({
  /** Theme provider dismiss button for toast */
  dismiss: 'Dismiss',
});

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const initialTheme = useGetThemeQuery({
    skip: isServer,
    context: {
      resolveWithUXP: true,
    },
  })?.data?.getTheme;

  const { data: themeData } = useOnThemeChangedSubscription({
    skip: isServer,
    context: {
      resolveWithUXP: true,
    },
  });
  const themeToUse = useMemo(() => {
    const defaultTheme = darkTheme;
    if (themeData?.themeChanged) {
      return themeData.themeChanged.includes('dark') ? darkTheme : lightTheme;
    }

    if (initialTheme) {
      return initialTheme.includes('dark') ? darkTheme : lightTheme;
    }

    return defaultTheme;
  }, [themeData, initialTheme]);

  const themeMap = THEME_CLASS_NAMES;
  useEffect(() => {
    const attrs = Object.values(themeMap);
    const applicableThemeValues = [themeMap.fio, themeMap[themeToUse]];
    updateDomThemeAttributes(
      applicableThemeValues,
      attrs,
      darkTheme,
      themeToUse
    );
    if (typeof window !== 'undefined') {
      document.body.classList.remove(styles.initialLoadBackground);
    }
  }, [themeMap, themeToUse]);

  return (
    <VaporProvider
      defaultBrand="fio"
      defaultDensity="compact"
      defaultSizing="small"
      defaultTheme={themeToUse}
      toastDefaultCloseLabel={TEXT.dismiss}
    >
      {children}
    </VaporProvider>
  );
};
